/**
 * Column definitions for the Product Information table view.
 *
 * The column set is the source-of-truth Create Product page field set
 * (react_app/src/Pages/DevlopmentOrder/ProductInformation/CreateProduct/index.tsx),
 * in the same order, with the same labels and gating.
 *
 * Built on the app-standard BombiixAGGrid. Each interactive column declares a
 * `cellType` (text / select) and is gated by `getCellReadOnly` so it renders a
 * plain value on list rows and an input/dropdown ONLY on the active add/edit row —
 * BombiixAGGrid swaps to its ReadonlyTextCell whenever getCellReadOnly is true.
 * Field-specific enable/disable rules ride on `getCellDisabled`.
 *
 * Per-column search is not a BombiixAGGrid feature; the grid is mounted with
 * showColumnMenu={false}, so no per-column search inputs appear.
 */

import type { BombiixColumnDef, BombiixRow } from '../../../../Components/BombiixAGGrid';
import ToggleCell from './ToggleCell';
import ImageCell from './ImageCell';
import NumberCell from './NumberCell';
import DescriptionCell from './DescriptionCell';
import TagsCell from './TagsCell';
import { DynamicColumn, InlineEditConfig, LockedField, LockedOption, MultiCustomAttribute, ProductItem, RefEntity, SelectOptionMap, TableRow, TFunc } from './types';
import { lockedIdsForField, isEditReviewedField } from './multiEdit';
import { scopeAllows, scopeHasId } from './useInlineRow';
import { getLengthLabel, getCurrentYearLabel } from '../../../../Components/helper';
import { additionalVariableOptionIds } from './cascadeOptions';
/** Auto-ID wiring passed to the Product Number cell (icon + generate handler). */
export interface AutoIdCellConfig {
  isAuto: (row: TableRow) => boolean;
  autoIdActive: boolean;
  onGenerate: (row: TableRow) => Promise<string | undefined>;
}

/** Tags wiring passed to the Tags cell (suggestion dropdown + lazy load, mirrors ReactTags). */
export interface TagsCellConfig {
  suggestions: Array<{ label: string; value: string }>;
  onLoadSuggestions: () => void;
}

/** Description-autocomplete wiring: matching products + the pick handler that auto-fills the row. */
export interface DescAutoFillConfig {
  suggestions: ProductItem[];
  onSelect: (product: ProductItem, row?: TableRow) => void;
  /** The draft whose description the suggestions were searched for; only that row shows them. */
  activeRowId?: string;
}

const LABEL = 'COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL';
const LICENSE_LABEL = 'LIKENESS_MASTER.ACTIVE_INACTIVE_CONFIRMATION';

/** Plain text/number fields that can be edited inline in the grid. */
export const TEXT_EDIT_FIELDS: string[] = ['number', 'description'];

/** Single-select dropdown fields edited inline. */
export const DROPDOWN_FIELDS: string[] = [
  'productNumberType',
  'brandId',
  'subBrandId',
  'licenseId',
  'likenessId',
  'supplierType',
  'catOneId',
  'catTwoId',
  'catThreeId',
  'catFourId',
  'seasonId',
  'month',
  'year',
  'transport',
  'status',
];

/** Multi-select fields edited inline — each cell holds an ARRAY of ids. */
export const MULTI_FIELDS: string[] = [
  'sizeRangeId',
  'sizeRangeSizes',
  'legLengths',
  'colourLibraries',
  'suppliers',
  'supplierFactories',
  'productTypeProcesses',
  'soldCountries',
  'additionalVariables',
];

/**
 * Whether a column stores an array rather than a single value. Multi-custom attributes are keyed
 * `mca_<id>` and are multi-select by definition, so they are matched by prefix.
 * @param colId the column id
 * @returns true when the cell holds an array
 */
export const isMultiValueField = (colId: string): boolean => MULTI_FIELDS.includes(colId) || colId.startsWith('mca_');

interface BuildColumnsOptions {
  optionsForRow?: (row: TableRow | undefined, field: string) => RefEntity[] | undefined;
  subBrandAccess: boolean;
  licensorAccess: boolean;
  /** Tenant "length visible" setting (ProductModal's lengthEnabled). Length column disabled when false. */
  lengthEnabled?: boolean;
  t: TFunc;
  edit: InlineEditConfig;
  selectOptions?: SelectOptionMap;
  autoId?: AutoIdCellConfig;
  tags?: TagsCellConfig;
  descAutoFill?: DescAutoFillConfig;
  /** Opens the "add option" modal for a field (colourway / custom attributes); enables the cell's "+" button. */
  onAddOption?: (field: string) => void;
  /** Supplier ids that already have samples on the row being edited; rendered disabled so they cannot be de-selected. */
  lockedSupplierIds?: string[];
  /** Asked on each pick in a warning-raising multi-select whether to write it at once, so its confirmation shows on the click. */
  onMultiPick?: (_rowId: string | number, _field: string, _value: unknown[]) => Promise<boolean>;
  /** Factory ids the row being edited already consumes downstream; rendered disabled so they cannot be de-selected. */
  lockedFactoryIds?: string[];
  /** Fired with the locked options when the user tries to remove a locked supplier or factory, so the page can say why. */
  onLockedRemoval?: (field: LockedField, lockedOptions: LockedOption[]) => void;
}

/**
 * Per-field disable conditions that depend only on other ROW values (parent-selection
 * cascade), mirroring the Create Product page. A field is disabled until its parent
 * selection is made. Access-flag gates (sub-brand / licensor / length) are layered on
 * top of these inside buildProductColumns (they need the caller's access flags).
 *
 * NOTE (table-view rule): where the modal HIDES a field, the table view SHOWS it and
 * DISABLES it under the same condition — so every gate here produces a disabled (greyed)
 * cell instead of a removed column.
 */
const FIELD_DISABLED: Record<string, (row: TableRow) => boolean> = {
  subBrandId: (row) => !row.brandId,
  licenseId: (row) => !row.isLicensedProduct,
  likenessId: (row) => !row.isLicensedProduct,
  catTwoId: (row) => !row.catOneId,
  catThreeId: (row) => !row.catTwoId,
  catFourId: (row) => !row.catThreeId,
  sizeRangeSizes: (row) => !Array.isArray(row.sizeRangeId) || row.sizeRangeId.length === 0,
  supplierFactories: (row) => !Array.isArray(row.suppliers) || row.suppliers.length === 0,
  productTypeProcesses: (row) => !row.supplierType,
};

/**
 * Read-only display text for a field: its `<field>__label` (the resolved name summary).
 * Used as the ReadonlyTextCell `accessor` so list rows show the name while the editable cell
 * still receives the raw id (needed to match its option).
 *
 * An id-backed column (dropdown/multi-select) NEVER falls back to its raw value: that value is
 * an id or id array, so stringifying it leaks a Mongo id — or a selection count — into a name
 * column. An empty label means "no resolvable name", which renders as the grid's `--`. Only a
 * plain text column, whose raw value IS its display text, falls back.
 */
const labelAccessor =
  (field: string, isIdBacked = true) =>
  (row: BombiixRow | undefined): string => {
    const label = row?.[`${field}__label`];
    if (label !== undefined && label !== null && label !== '') return String(label);
    if (isIdBacked) return '';
    const raw = row?.[field];
    if (Array.isArray(raw)) return '';
    return raw === undefined || raw === null ? '' : String(raw);
  };

/**
 * Build the static BombiixAGGrid column definitions for the Product Information table view.
 * Text fields render an editable input and dropdown/multi fields render a select — but
 * only on the active add/edit row; every other row shows the plain value.
 */
export const buildProductColumns = ({
  subBrandAccess,
  licensorAccess,
  lengthEnabled = true,
  t,
  edit,
  selectOptions = {},
  optionsForRow,
  autoId,
  tags,
  descAutoFill,
  onAddOption,
  lockedSupplierIds = [],
  onMultiPick,
  lockedFactoryIds = [],
  onLockedRemoval,
}: BuildColumnsOptions): BombiixColumnDef<TableRow>[] => {
  const readOnlyOnList = (row: TableRow) => !edit.isInlineRow(row);

  /**
   * Access-flag disable gates layered on top of the row-cascade map. These mirror the
   * modal's HIDE conditions, converted to DISABLE for the table view:
   *  - Sub Brand shown always, disabled unless the tenant has sub-brand access AND a brand is chosen.
   *  - Licensor/License/Likeness shown always, disabled unless the tenant has licensor access
   *    (license/likeness additionally need the product flagged licensed — via FIELD_DISABLED).
   *  - Length shown always, disabled when the tenant's length-visible setting is off.
   */
  const ACCESS_DISABLED: Record<string, (row: TableRow) => boolean> = {
    /*
     * Product Number Type only offers Auto when an auto-id config is active (universal or any
     * brand) — the same gate single edit puts on its dropdown. Without it the grid lets a user
     * pick Auto on a tenant that cannot generate a number.
     *
     * Single edit additionally forces a stored Auto back to Manual when the config is off. That is
     * not done here: silently rewriting a field on rows the user never touched would mark them
     * dirty and save the change with the batch.
     */
    productNumberType: () => !(autoId?.autoIdActive ?? false),
    subBrandId: () => !subBrandAccess,
    licensorId: () => !licensorAccess,
    licenseId: () => !licensorAccess,
    likenessId: () => !licensorAccess,
    legLengths: () => !lengthEnabled,
  };

  const fieldDisabled = (field: string) => (row: TableRow) => (FIELD_DISABLED[field]?.(row) ?? false) || (ACCESS_DISABLED[field]?.(row) ?? false);
  const fieldError = (field: string) => (row: TableRow) => edit.isInlineRow(row) ? edit.getError(row, field) : undefined;

  /**
   * The Product Number column: on the inline row it renders NumberCell (input + auto-id
   * generate icon when the row's type is Auto); every other row shows the number as a link
   * to the product (BM-10020).
   *
   * Deliberately declares NO `cellType`: BombiixAGGrid swaps any interactive-cellType column
   * to ReadonlyTextCell on read-only rows, which would drop the link. Without a cellType the
   * grid always renders NumberCell, which picks link vs input itself via `isInlineRow`.
   */
  const numberColumn: BombiixColumnDef<TableRow> = {
    field: 'number',
    headerName: t(`${LABEL}.PRODUCT_NUMBER`),
    minWidth: 160,
    required: true,
    cellRenderer: NumberCell,
    /*
     * Opt in to fill and clipboard explicitly. The grid derives that from `cellType`, which this
     * column does not set because it renders NumberCell (input plus the auto-id generate button)
     * rather than a plain text cell — so without this flag the column is silently skipped by both
     * the fill handle and paste, while still being typeable.
     */
    fillable: true,
    cellProps: {
      isAuto: autoId?.isAuto ?? (() => false),
      autoIdActive: autoId?.autoIdActive ?? false,
      onGenerate: autoId?.onGenerate ?? (async () => undefined),
      isInlineRow: edit.isInlineRow,
    },
    getCellReadOnly: readOnlyOnList,
    /*
     * An auto-numbered row's input is disabled (NumberCell), so fill and paste must be blocked
     * there too — otherwise they would write a number the user could not have typed, and overwrite
     * one the counter issued.
     */
    getCellDisabled: (row: TableRow) => Boolean(autoId?.isAuto?.(row)),
    getCellError: fieldError('number'),
  };

  /**
   * The description column: plain text on list rows; on the inline row a text input with a
   * 200-char counter plus the "add description as tags" checkbox (mirrors the Create modal).
   */
  const descriptionColumn: BombiixColumnDef<TableRow> = {
    field: 'description',
    headerName: t(`${LABEL}.PRODUCT_DESCRIPTION`),
    minWidth: 260,
    required: true,
    cellType: 'text',
    cellRenderer: DescriptionCell,
    cellProps: {
      suggestions: descAutoFill?.suggestions || [],
      onSelectSuggestion: descAutoFill?.onSelect ?? (() => undefined),
      suggestionRowId: descAutoFill?.activeRowId,
    },
    getCellReadOnly: readOnlyOnList,
    getCellError: fieldError('description'),
  };

  /** An editable single-select dropdown column. */
  const selectColumn = (field: string, headerKey: string, overrides: Partial<BombiixColumnDef<TableRow>> = {}): BombiixColumnDef<TableRow> => ({
    field,
    headerName: t(headerKey),
    minWidth: 140,
    /*
     * No valueGetter: the editable SelectInputCell needs the raw id (row[field]) to match its
     * option; the read-only ReadonlyTextCell shows the name via the `accessor` cellProp.
     */
    cellType: 'select',
    cellProps: { options: selectOptions[field] || [], isClearable: true, accessor: labelAccessor(field) },
    getCellProps: (row: TableRow) => {
      const rowOptions = optionsForRow?.(row, field);
      return rowOptions ? { options: rowOptions } : {};
    },
    getCellReadOnly: readOnlyOnList,
    getCellDisabled: fieldDisabled(field),
    getCellError: fieldError(field),
    ...overrides,
  });

  /** An editable multi-select column. */
  const multiColumn = (field: string, headerKey: string, overrides: Partial<BombiixColumnDef<TableRow>> = {}): BombiixColumnDef<TableRow> => ({
    field,
    headerName: t(headerKey),
    minWidth: 160,
    cellType: 'select',
    /*
     * Grey out values the product already consumes downstream — a supplier on a live Purchase
     * Order, a colourway used by a BOM/sample/costing/design. They stay selected but cannot be
     * removed, matching the Supplier(s) and Colourway(s) dropdowns on the single-edit page.
     * Fields with no lock get an empty set, so this costs nothing for the other multi-selects.
     */
    getCellProps: (row: TableRow) => {
      const locked = lockedIdsForField(row, field);
      const rowOptions = optionsForRow?.(row, field);
      return {
        ...(rowOptions ? { options: rowOptions } : {}),
        ...(locked.size ? { isOptionDisabled: (option: { _id?: unknown }) => locked.has(String(option?._id)) } : {}),
        ...(onMultiPick && isEditReviewedField(field) ? { onMultiPick } : {}),
      };
    },
    /* Master-design multi-select menu extras, matching the Create Product form's multi-selects
       (showSearch + "Select All"): in-menu search and a Select All row above the options. */
    cellProps: {
      options: selectOptions[field] || [],
      isMulti: true,
      accessor: labelAccessor(field),
      showSearch: true,
      selectAllLabel: t('COMMON_LABEL.SELECT_ALL'),
      clearAllLabel: t('COMMON_LABEL.CLEAR_ALL'),
    },
    getCellReadOnly: readOnlyOnList,
    getCellDisabled: fieldDisabled(field),
    getCellError: fieldError(field),
    ...overrides,
  });

  /** The image column: read-only thumbnail on list rows, click-to-upload on the inline row. */
  const imageColumn: BombiixColumnDef<TableRow> = {
    field: 'image',
    headerName: t(`${LABEL}.IMAGE`),
    minWidth: 90,
    maxWidth: 90,
    cellRenderer: ImageCell,
    cellProps: { isInlineRow: edit.isInlineRow },
  };

  /**
   * The tags column: read-only badges on list rows; on the inline row a searchable
   * Creatable multi-select (type + Enter to add a new tag, or pick a suggestion), with
   * selected tags shown as badges inline to the right of the input (mirrors ReactTags).
   */
  const tagsColumn: BombiixColumnDef<TableRow> = {
    field: 'tags',
    headerName: t('COMMON_LABEL.TAGS'),
    minWidth: 280,
    cellRenderer: TagsCell,
    cellProps: {
      isInlineRow: edit.isInlineRow,
      suggestions: tags?.suggestions || [],
      onLoadSuggestions: tags?.onLoadSuggestions ?? (() => undefined),
      placeholder: t(`${LABEL}.ADD_TAG`),
    },
  };

  /**
   * The licensor column pairs an "is licensed" toggle with a licensor dropdown on the inline
   * row. Shown always; disabled (toggle + dropdown greyed) unless the tenant has licensor
   * access — mirrors the modal's `licensorAccess`-gated block, converted hide→disable.
   */
  const licensorColumn: BombiixColumnDef<TableRow> = {
    field: 'licensorId',
    headerName: t(`${LICENSE_LABEL}.LICENSOR`),
    minWidth: 200,
    cellRenderer: ToggleCell,
    cellProps: {
      options: selectOptions.licensorId || [],
      toggleField: 'isLicensedProduct',
      isInlineRow: edit.isInlineRow,
      accessor: labelAccessor('licensorId'),
      placeholder: `${t(`${LABEL}.SELECT`)}${t(`${LICENSE_LABEL}.LICENSOR`)}`,
    },
    getCellReadOnly: readOnlyOnList,
    getCellDisabled: fieldDisabled('licensorId'),
    getCellError: fieldError('licensorId'),
  };

  /**
   * The Supplier(s) column pairs the supplier multi-select with the "Visible to Supplier" switch
   * (BM-9574): the switch renders on the inline add/edit row only and is saved with the row. Unlike
   * Licensor, the switch neither disables nor clears the multi-select — visibility governs who may
   * SEE the product, not who may be assigned to it, so suppliers stay editable while it is off and
   * are never dropped (the save payload derives `followers` from them). This matches the Create /
   * Edit Product page, whose Supplier(s) field carries no `isDisabled` guard at all. The info icon
   * beside the switch carries the "Visible to Supplier" hover tooltip.
   *
   * No `cellType`, as with Licensor: ToggleCell renders the read-only text itself on list rows.
   *
   * BM-9623: the header reads "Supplier" (the label-only key), so the dropdown placeholder is
   * passed explicitly — SelectInputCell falls back to the header name when none is given, which
   * would have turned the placeholder into "Supplier" instead of "Select Supplier(s)".
   */
  const suppliersColumn: BombiixColumnDef<TableRow> = {
    field: 'suppliers',
    headerName: t('product-information:PRODUCT_MODAL.SUPPLIER_LABEL'),
    minWidth: 200,
    cellRenderer: ToggleCell,
    fillable: true,
    cellProps: {
      options: selectOptions.suppliers || [],
      isMulti: true,
      accessor: labelAccessor('suppliers'),
      showSearch: true,
      selectAllLabel: t('COMMON_LABEL.SELECT_ALL'),
      clearAllLabel: t('COMMON_LABEL.CLEAR_ALL'),
      placeholder: `${t(`${LABEL}.SELECT`)}${t(`${LABEL}.SELECT_SUPPLIER`)}`,
      toggleField: 'visibleToSupplier',
      isInlineRow: edit.isInlineRow,
      clearSelectionOnOff: false,
      disableSelectionOnOff: false,
      toggleLabel: t(`${LABEL}.VISIBLE_TO_SUPPLIER`),
      toggleInfo: true,
      /*
       * A supplier with samples on this product stays selected and cannot be de-selected.
       * Ids are compared as strings: option ids and sample supplier ids are both ObjectId strings.
       */
      isOptionDisabled: (option: { value?: string; _id?: string }) => lockedSupplierIds.includes(String(option?.value ?? option?._id ?? '')),
      onLockedRemoval: (lockedOptions: LockedOption[]) => onLockedRemoval?.('suppliers', lockedOptions),
    },
    /* The same per-row props `multiColumn` supplies: the row's own supplier list, and the in-use
       suppliers a bulk-edit row must keep. This column is built by hand for its toggle, so it has
       to ask for them itself. */
    getCellProps: (row: TableRow) => {
      const locked = lockedIdsForField(row, 'suppliers');
      const rowOptions = optionsForRow?.(row, 'suppliers');
      return {
        ...(rowOptions ? { options: rowOptions } : {}),
        ...(locked.size ? { isOptionDisabled: (option: { _id?: unknown }) => locked.has(String(option?._id)) } : {}),
      };
    },
    getCellReadOnly: readOnlyOnList,
    getCellDisabled: fieldDisabled('suppliers'),
    getCellError: fieldError('suppliers'),
  };

  /*
   * Table-view rule: every field is SHOWN; access-gated fields (sub-brand, licensor trio) are
   * rendered always and DISABLED via fieldDisabled when their access flag is off — instead of
   * being conditionally omitted like the modal. Column ORDER matches the Create Product page.
   */
  const cols: BombiixColumnDef<TableRow>[] = [
    imageColumn,
    selectColumn('productNumberType', `${LABEL}.PRODUCT_NUMBER_TYPE`, { required: true }),
    numberColumn,
    descriptionColumn,
    selectColumn('brandId', `${LABEL}.BRAND`, { required: true }),
    selectColumn('subBrandId', 'SUB_BRAND.SUBBRAND'),
    licensorColumn,
    selectColumn('licenseId', `${LICENSE_LABEL}.LICENSE`),
    selectColumn('likenessId', `${LICENSE_LABEL}.LIKENESS`),
  ];

  cols.push(
    selectColumn('supplierType', `${LABEL}.PRODUCT_TYPE`, { required: true }),
    selectColumn('catOneId', `${LABEL}.CATEGO0RY_1`, { required: true }),
    selectColumn('catTwoId', `${LABEL}.CATEGORIES_2`, { required: true }),
    selectColumn('catThreeId', `${LABEL}.CATEGORIES_3`),
    selectColumn('catFourId', `${LABEL}.CATEGORY4`),
    selectColumn('seasonId', `${LABEL}.SEASON`),
    selectColumn('month', `${LABEL}.MONTH`),
    selectColumn('year', `${LABEL}.YEAR`, {
      cellProps: { options: selectOptions.year || [], isClearable: true, accessor: labelAccessor('year'), scrollToLabel: getCurrentYearLabel() },
    }),
    multiColumn('sizeRangeId', `${LABEL}.SIZE_RANGE_TITLE`),
    multiColumn('sizeRangeSizes', `${LABEL}.SIZES`),
    multiColumn('legLengths', `${LABEL}.LENGTH`, { headerName: getLengthLabel(t(`${LABEL}.LENGTH`)) }),
    multiColumn('colourLibraries', `${LABEL}.COLOUR_WAYS`, {
      /* Overrides replace multiColumn's cellProps wholesale, so restate the master-design menu
         extras (search + Select All) here alongside this column's "+" add button. */
      cellProps: {
        options: selectOptions.colourLibraries || [],
        isMulti: true,
        accessor: labelAccessor('colourLibraries'),
        showSearch: true,
        selectAllLabel: t('COMMON_LABEL.SELECT_ALL'),
        clearAllLabel: t('COMMON_LABEL.CLEAR_ALL'),
        ...(onAddOption ? { onAdd: onAddOption, addLabel: t(`${LABEL}.ADD`) } : {}),
      },
    }),
    suppliersColumn,
    /* BM-9623: the header reads "Factory" (label-only key), while the inline-row dropdown keeps the
       plural "Select Factory(s)" the Create Product page uses. Overrides replace multiColumn's
       cellProps wholesale, so the master-design menu extras are restated alongside the placeholder. */
    multiColumn('supplierFactories', 'product-information:PRODUCT_MODAL.FACTORY_LABEL', {
      cellProps: {
        options: selectOptions.supplierFactories || [],
        isMulti: true,
        accessor: labelAccessor('supplierFactories'),
        showSearch: true,
        selectAllLabel: t('COMMON_LABEL.SELECT_ALL'),
        clearAllLabel: t('COMMON_LABEL.CLEAR_ALL'),
        placeholder: `${t(`${LABEL}.SELECT`)}${t(`${LABEL}.SELECT_FACTORY`)}`,
        isOptionDisabled: (option: { value?: string; _id?: string }) =>
          lockedFactoryIds.includes(String(option?.value ?? option?._id ?? '')),
        onLockedRemoval: (lockedOptions: LockedOption[]) => onLockedRemoval?.('supplierFactories', lockedOptions),
      },
    }),
    selectColumn('transport', 'BOM_TYPE_FORM.TRANSPORT', { minWidth: 160, dppRequired: true }),
    multiColumn('productTypeProcesses', `${LABEL}.PRODUCT_TYPE_PROCESS`, { minWidth: 180 }),
    multiColumn('soldCountries', `${LABEL}.PRODUCT_SOLD_IN`, { minWidth: 180 }),
    multiColumn('additionalVariables', 'RETAIL_IDENTIFIER.BATCH.COLUMN.VARIABLE_2', {
      minWidth: 180,
      valueGetter: (params) => additionalVariableOptionIds(params.data?.additionalVariables),
    }),
    selectColumn('status', `${LABEL}.STATUS`),
    tagsColumn,
  );

  return cols;
};

interface BuildDynamicOptions {
  t: TFunc;
  edit: InlineEditConfig;
  /** Opens the "add option" modal for a dropdown/radio single custom attribute (its "| +" button). */
  onAddOption?: (field: string) => void;
}

/**
 * Build one column per single custom attribute, mirroring ProductModal's SHOW/HIDE model.
 * The caller (columnDefs) only passes ACTIVE attributes and only when the inline row already
 * has brand + product type — so these columns exist ONLY when the modal would render them
 * (the modal's dynamicFields is empty without brand+PT and skips inactive attributes). Here we
 * therefore do NOT re-gate on isActive/brand/PT; the remaining rule is per-type editability:
 * a TEXT field is editable only when its "Editable field" toggle is on
 * (`textFieldConfig.isEditable === true` — a never-toggled attribute carries no flag and stays
 * disabled, as on the Edit Product page), and a DATE-PICKER is disabled only by an explicit
 * `datePickerConfig.isEditable === false`. Dropdown/radio have no such flag. List rows always
 * render read-only text.
 */
/**
 * Resolve a dynamic-attribute entry's id (mirrors rowMapper's dynamicAttrId): the id may live
 * on `_id` directly or under a populated/plain `colId`. Every source that keys a `dyn_<id>`
 * column (buildDynamicColumns, columnDefs union, rowMapper) MUST resolve it the same way.
 */
const dynAttrId = (entry: DynamicColumn): string => {
  if (entry?._id) return String(entry._id);
  const colId = entry?.colId as { _id?: string } | string | undefined;
  if (colId && typeof colId === 'object' && colId._id) return String(colId._id);
  if (typeof colId === 'string') return colId;
  return '';
};

export const buildDynamicColumns = (
  dynamicColumn: DynamicColumn[] | undefined,
  { t, edit, onAddOption }: BuildDynamicOptions,
): BombiixColumnDef<TableRow>[] => {
  if (!Array.isArray(dynamicColumn)) return [];
  return dynamicColumn.map((field): BombiixColumnDef<TableRow> => {
    const attrId = dynAttrId(field);
    const key = `dyn_${attrId}`;
    /*
     * Per-type editability (mirrors the modal): text/date-picker honour their own isEditable flag;
     * dropdown/radio have none.
     */
    const typeEditable =
      field.type === 'text' ? field.textFieldConfig?.isEditable === true : field.type === 'date-picker' ? field.datePickerConfig?.isEditable !== false : true;
    /*
     * SHOW + DISABLE: the column is ALWAYS present (every custom field visible by default on add).
     * Scope is resolved PER ROW, not once per column: each row enables this cell only when the
     * attribute's own brand and productType lists admit that row's brand + product type. A grid
     * holds rows of differing product types at once, so a single column-wide flag would enable an
     * Accessories-only attribute on a Footwear row. Text/date additionally honour isEditable.
     */
    const activeEditable = Boolean(field.isActive) && typeEditable;
    /*
     * Dropdown/radio attributes edit as a single-select bound to their active option values
     * (the row stores the selected option id → saved as valueId). Text edits as text and
     * date-picker edits as a date cell (both stored string → saved as valueText; the date
     * cell stores an ISO day string). Mirrors ProductModal's per-type custom-attr cells.
     */
    /* A radio-button attribute edits as an actual radio group, not a single-select — it had been
       lumped in with dropdown, which ignored the type chosen in System Masters. Both still store the
       selected option id, so the saved shape is unchanged. */
    const isRadio = field.type === 'radio-button';
    const isOption = field.type === 'dropdown';
    const isDate = field.type === 'date-picker';
    /*
     * A text attribute's character limit, applied exactly as the Create Product page applies it:
     * `isExceedable` lets the user type past it (the over-limit value is then rejected on save),
     * otherwise the input caps typing at the limit.
     */
    const isText = !isRadio && !isOption && !isDate;
    const charLimit = field.textFieldConfig?.isExceedable ? undefined : field.textFieldConfig?.charLimit;
    const optionValues = activeOptionValues(field);
    return {
      field: key,
      headerName: field.keyName || t(`${LABEL}.VARIABLES`),
      /* Radios need more room than a collapsed select — several labels sit side by side. */
      minWidth: isRadio ? 200 : 140,
      cellType: isRadio ? 'radioGroup' : isOption ? 'select' : isDate ? 'date' : 'text',
      ...(isRadio ? { cellProps: { options: optionValues, accessor: labelAccessor(key) } } : {}),
      ...(isText && charLimit ? { cellProps: { maxLength: charLimit } } : {}),
      ...(isOption
        ? {
            cellProps: {
              options: optionValues,
              isClearable: true,
              accessor: labelAccessor(key),
              showSearch: true,
              ...(onAddOption ? { onAdd: onAddOption, addLabel: t('COMMON_LABEL.ADD') } : {}),
            },
          }
        : {}),
      getCellReadOnly: (row: TableRow) => !edit.isInlineRow(row),
      getCellDisabled: (row: TableRow) =>
        !activeEditable ||
        !row.supplierType ||
        !row.brandId ||
        !scopeAllows(field.productType, String(row.supplierType)) ||
        !scopeAllows(field.brand, String(row.brandId)),
      getCellError: (row: TableRow) => (edit.isInlineRow(row) ? edit.getError(row, key) : undefined),
    };
  });
};

interface BuildMultiCustomOptions {
  edit: InlineEditConfig;
  selectOptions?: SelectOptionMap;
  t?: TFunc;
  /** Opens the "add multi-custom option" modal for this attribute; enables the cell's "+" button. */
  onAddOption?: (field: string) => void;
}

/**
 * Build one multi-select column per multi custom attribute (SHOW + DISABLE model). Every
 * attribute is shown as a column; the cell is disabled unless the attribute is active AND the
 * row's product type is in its `productType` list — mirroring the condition ProductModal uses to
 * render a multiDynamicAttribute field, converted hide→disable. Each carries a "+" add button.
 */
export const buildMultiCustomColumns = (
  attributes: MultiCustomAttribute[] | undefined,
  { edit, selectOptions = {}, t, onAddOption }: BuildMultiCustomOptions,
): BombiixColumnDef<TableRow>[] => {
  if (!Array.isArray(attributes)) return [];
  return attributes.map((attribute): BombiixColumnDef<TableRow> => {
    const field = `mca_${attribute._id}`;
    return {
      field,
      headerName: attribute.keyName || field,
      minWidth: 160,
      cellType: 'select',
      cellProps: {
        options: selectOptions[field] || [],
        isMulti: true,
        accessor: labelAccessor(field),
        /* Master-design multi-select menu extras (search + "Select All"), matching the Create
           Product form's multi-selects. "+" add button stays where the caller wires onAddOption. */
        showSearch: true,
        ...(t ? { selectAllLabel: t('COMMON_LABEL.SELECT_ALL'), clearAllLabel: t('COMMON_LABEL.CLEAR_ALL') } : {}),
        ...(onAddOption ? { onAdd: onAddOption, addLabel: t ? t('COMMON_LABEL.ADD') : 'Add' } : {}),
      },
      getCellReadOnly: (row: TableRow) => !edit.isInlineRow(row),
      /*
       * Multi custom attributes carry no brand in their master (MultiCustomAttribute has only
       * productType), so product type is the whole scope — and unlike single custom attributes an
       * empty list means "no product type assigned", which hides the field in the product form.
       */
      getCellDisabled: (row: TableRow) => !attribute.isActive || !scopeHasId(attribute.productType, String(row.supplierType)),
    };
  });
};

/**
 * The options a dropdown / radio custom attribute offers: its active values, exactly as its cell lists them.
 * @param field - the custom attribute.
 * @returns one `{ _id, name }` per active value.
 */
function activeOptionValues(field: DynamicColumn): RefEntity[] {
  return (field.values || []).filter((option) => option.isActive).map((option) => ({ _id: option._id, name: option.keyName ?? '' }));
}

/**
 * Option lists for every dropdown / radio custom attribute, keyed by its `dyn_<id>` column. Joined into
 * the table's option map so a paste resolves a label to the option id the cell stores, and an edit
 * refreshes the label a copy reads — without them a paste wrote the label itself into the id field.
 * @param attributes - the single custom-attribute master the columns are built from.
 * @returns `dyn_<id>` -> that attribute's active options.
 */
export function buildDynamicOptionMap(attributes: DynamicColumn[] | undefined): SelectOptionMap {
  const optionMap: SelectOptionMap = {};
  (attributes ?? []).forEach((attribute) => {
    if (attribute.type === 'dropdown' || attribute.type === 'radio-button') optionMap[`dyn_${dynAttrId(attribute)}`] = activeOptionValues(attribute);
  });
  return optionMap;
}

/**
 * Whether a column is a date-picker custom attribute, whose cell stores an ISO `yyyy-mm-dd` day.
 * @param field - the column field.
 * @param attributes - the single custom-attribute master.
 * @returns true for a `dyn_<id>` date column.
 */
export function isDynamicDateField(field: string, attributes: DynamicColumn[] | undefined): boolean {
  return (attributes ?? []).some((attribute) => attribute.type === 'date-picker' && `dyn_${dynAttrId(attribute)}` === field);
}
